import { AfterViewInit, Component, computed, ElementRef, inject, input, OnDestroy, viewChild } from '@angular/core';

import { Media, MediaView } from '@eiq/files/models';

import { FileType } from '@empathy/global/data';

import { SurveyViewerStateService } from '../../services/survey-viewer-state.service';

@Component({
  selector: 'empathy-media-view',
  templateUrl: 'media-view.component.html',
  styleUrls: ['./media-view.component.scss']
})
export class MediaViewComponent implements AfterViewInit, OnDestroy {
  media = input<Media>();
  mediaView = input<MediaView>();
  url = input<string>();
  isExpanded = input<boolean>();
  autoplay = input<boolean>(false);

  readonly videoRef$ = viewChild<ElementRef<HTMLVideoElement>>('videoEl');

  mediaToDisplay$ = computed<Media>(() => {
    if (this.media()) {
      return this.media();
    }

    return { mediaId: this.mediaView().id, name: this.mediaView().name, type: this.mediaView().type, url: this.url() } as Media;
  });
  isBlobUrl$ = computed(() => this.mediaToDisplay$().url?.startsWith('blob:') ?? false);

  fileType = FileType;

  private surveyViewerStateService = inject(SurveyViewerStateService);

  private readonly toleranceSec = 1;

  private maxWatched = 0;
  private lastTime = 0;

  private detach?: () => void;

  ngAfterViewInit(): void {
    const video = this.videoRef$()?.nativeElement;
    if (!video) {
      return;
    }

    this.detach = this.attachListeners(video);
  }

  ngOnDestroy(): void {
    this.detach?.();
    this.detach = undefined;
  }

  pause(): void {
    this.videoRef$()?.nativeElement.pause();
  }

  private canSeekForward(): boolean {
    return this.surveyViewerStateService.mediaRequiresCompletion(this.media().mediaId);
  }

  private attachListeners(video: HTMLVideoElement) {
    let isSeeking = false;

    const clampToMax = (): void => {
      if (!this.canSeekForward()) {
        return;
      }

      if (video.currentTime > this.maxWatched + this.toleranceSec) {
        video.currentTime = this.maxWatched;
      }
    };

    const onSeeking = (): void => {
      isSeeking = true;
      clampToMax();
    };

    const onSeeked = (): void => {
      clampToMax();
      isSeeking = false;
      this.lastTime = video.currentTime;
    };

    const onTimeUpdate = (): void => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        return;
      }

      clampToMax();

      const currentTime = video.currentTime;
      const delta = currentTime - this.lastTime;

      if (!isSeeking && delta >= -this.toleranceSec) {
        this.maxWatched = Math.max(this.maxWatched, currentTime);
      }

      this.lastTime = video.currentTime;
    };

    const onEnded = (): void => {
      this.surveyViewerStateService.markMediaCompleted(this.media().mediaId);
    };

    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);

    return (): void => {
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }
}
